import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import {
  CheckCircle2, Circle, ChevronRight, X, Sparkles, BedDouble,
  ScanLine, CalendarDays, FileSpreadsheet, Building2,
} from "lucide-react";
import OnboardingWizard from "@/components/OnboardingWizard";

const DISMISS_KEY = "xenia_checklist_dismissed";

/**
 * First-steps checklist shown on the Dashboard until the host has completed
 * the 5 core setup actions. Lightweight, no modal — embedded inline so users
 * who skip the wizard still get guidance.
 *
 * Items are computed from /stats/overview counts.
 * The widget hides when:
 *   - the user explicitly dismisses it,  OR
 *   - all items are complete.
 */
export default function OnboardingChecklist({ propertyName }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [wizardKey, setWizardKey] = useState(0);
  const [openWizard, setOpenWizard] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/stats/overview").then((r) => {
      if (!cancelled) setStats(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [propertyName]);

  const items = useMemo(() => {
    const s = stats || {};
    return [
      {
        key: "property",
        done: !!propertyName,
        label: t("onboarding_chk.property"),
        sub: t("onboarding_chk.property_sub"),
        icon: Building2,
        action: () => nav("/app/properties"),
      },
      {
        key: "rooms",
        done: (s.rooms_total ?? 0) > 0,
        label: t("onboarding_chk.rooms"),
        sub: t("onboarding_chk.rooms_sub"),
        icon: BedDouble,
        action: () => { setWizardKey((k) => k + 1); setOpenWizard(true); },
      },
      {
        key: "guests",
        done: (s.guests_total ?? 0) > 0,
        label: t("onboarding_chk.guests"),
        sub: t("onboarding_chk.guests_sub"),
        icon: ScanLine,
        action: () => nav("/app/scanner"),
      },
      {
        key: "bookings",
        done: (s.bookings_total ?? 0) > 0,
        label: t("onboarding_chk.bookings"),
        sub: t("onboarding_chk.bookings_sub"),
        icon: CalendarDays,
        action: () => nav("/app/calendar"),
      },
      {
        key: "alloggiati",
        done: (s.alloggiati_sent_total ?? 0) > 0,
        label: t("onboarding_chk.alloggiati"),
        sub: t("onboarding_chk.alloggiati_sub"),
        icon: FileSpreadsheet,
        action: () => nav("/app/alloggiati"),
      },
    ];
  }, [stats, propertyName, t, nav]);

  const total = items.length;
  const completed = items.filter((i) => i.done).length;
  const allDone = completed === total;

  if (dismissed) return null;
  if (!stats) return null;
  if (allDone) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <>
      {openWizard && (
        <OnboardingWizard key={wizardKey} initialOpen onClose={() => setOpenWizard(false)} />
      )}
      <div
        className="border-2 border-blue-200 bg-blue-50/60 rounded-sm p-6 mb-10 relative"
        data-testid="onboarding-checklist"
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-stone-400 hover:text-stone-700 p-1"
          aria-label={t("onboarding_chk.dismiss")}
          data-testid="onboarding-checklist-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <Sparkles className="w-6 h-6 text-blue-700 flex-shrink-0 mt-1" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <p className="text-xs tracked text-blue-700 mb-1">
              {t("onboarding_chk.label")}
            </p>
            <h3 className="font-serif text-2xl tracking-tight">
              {t("onboarding_chk.title")}
            </h3>
            <p className="text-stone-600 text-sm mt-1">
              {t("onboarding_chk.progress", { done: completed, total })}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div
          className="h-1.5 bg-stone-200 rounded-full overflow-hidden mb-5"
          data-testid="onboarding-checklist-progress"
        >
          <div
            className="h-full bg-blue-700 transition-all duration-500"
            style={{ width: `${(completed / total) * 100}%` }}
          />
        </div>

        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.key}>
              <button
                onClick={it.done ? undefined : it.action}
                disabled={it.done}
                data-testid={`onboarding-chk-${it.key}`}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                  it.done
                    ? "text-stone-500"
                    : "bg-white hover:bg-blue-100/40 border border-stone-200 cursor-pointer"
                }`}
              >
                {it.done ? (
                  <CheckCircle2 className="w-5 h-5 text-green-700 flex-shrink-0" strokeWidth={1.8} />
                ) : (
                  <Circle className="w-5 h-5 text-stone-400 flex-shrink-0" strokeWidth={1.8} />
                )}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${it.done ? "line-through" : "font-medium text-stone-900"}`}>
                    {it.label}
                  </div>
                  {!it.done && (
                    <div className="text-xs text-stone-500 mt-0.5">{it.sub}</div>
                  )}
                </div>
                {!it.done && (
                  <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
